/**
 * Portal domain scope.
 *
 * A portal session covers every domain in the customer organisation, so most
 * views need to know whether the customer is looking at the whole estate or one
 * domain. `selectedDomainId === null` means "All domains".
 *
 * Two distinct ideas, deliberately separate:
 *   - `selectedDomainId` — the filter for aggregate views (dashboard, reports,
 *     senders, alerts). null is a legitimate value meaning "everything".
 *   - `actionableDomainId` — the subject for per-domain features (policy
 *     rollout, DNS re-check, notification settings). Those cannot act on "all",
 *     so when nothing is selected they fall back to the single domain if the
 *     org has exactly one, and otherwise stay null so the page can ask the
 *     customer to pick one.
 *
 * The selection is persisted per browser so a customer who works on one domain
 * is not re-picking it on every visit, and is validated against the live domain
 * list so a removed domain cannot strand the UI on an id the API now 404s.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { portalApi, type PortalDomain } from "./portal-api";

const STORAGE_KEY = "ddmarc.portal.selectedDomainId";

interface PortalScopeContextValue {
  domains: PortalDomain[];
  isLoading: boolean;
  selectedDomainId: number | null;
  setSelectedDomainId: (id: number | null) => void;
  selectedDomain: PortalDomain | null;
  actionableDomainId: number | null;
  /** True when the org has more than one domain — the switcher is only useful then. */
  hasMultipleDomains: boolean;
}

const PortalScopeContext = createContext<PortalScopeContextValue>({
  domains: [],
  isLoading: true,
  selectedDomainId: null,
  setSelectedDomainId: () => {},
  selectedDomain: null,
  actionableDomainId: null,
  hasMultipleDomains: false,
});

function readStoredId(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : null;
  } catch {
    // Private-mode / disabled storage — scope just doesn't persist.
    return null;
  }
}

export function PortalScopeProvider({ children }: { children: ReactNode }) {
  const [selectedDomainId, setSelectedDomainIdState] = useState<number | null>(
    readStoredId
  );

  const { data, isLoading } = useQuery({
    queryKey: ["portal", "domains", "scope"],
    // size covers the largest customer estates in one page; the switcher needs
    // the full list, not a paginated slice.
    queryFn: () => portalApi.listDomains({ size: 100 }),
  });

  const domains = useMemo(() => data?.items ?? [], [data]);

  const setSelectedDomainId = (id: number | null) => {
    setSelectedDomainIdState(id);
    try {
      if (id === null) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, String(id));
    } catch {
      // Non-fatal: selection still applies for this session.
    }
  };

  // Drop a stored selection that no longer exists (domain removed, or the
  // customer signed in to a different org on this browser). Without this the
  // whole portal would render empty against an id the API rejects.
  useEffect(() => {
    if (isLoading || selectedDomainId === null) return;
    if (!domains.some((d) => d.id === selectedDomainId)) {
      setSelectedDomainId(null);
    }
  }, [isLoading, domains, selectedDomainId]);

  const selectedDomain =
    domains.find((d) => d.id === selectedDomainId) ?? null;

  const value: PortalScopeContextValue = {
    domains,
    isLoading,
    selectedDomainId,
    setSelectedDomainId,
    selectedDomain,
    actionableDomainId:
      selectedDomainId ?? (domains.length === 1 ? domains[0].id : null),
    hasMultipleDomains: domains.length > 1,
  };

  return (
    <PortalScopeContext.Provider value={value}>
      {children}
    </PortalScopeContext.Provider>
  );
}

export function usePortalScope(): PortalScopeContextValue {
  return useContext(PortalScopeContext);
}
